/**
 * ScrollRevealManager
 *
 * A lightweight, performant global manager for scroll-triggered CSS animations.
 * Uses IntersectionObserver to detect when elements enter the viewport, and
 * MutationObserver to seamlessly handle elements added or removed dynamically.
 *
 * Respects user preferences by early-exiting if 'prefers-reduced-motion' is enabled.
 *
 * @example
 * // 1. Add animation initial states in your CSS:
 * // .reveal-up { opacity: 0; transform: translateY(2rem); }
 * // .reveal-up.is-visible { animation: reveal 0.4s ease forwards; }
 * //
 * // 2. Initialize the manager:
 * // new ScrollRevealManager({ selectors: ['.reveal-up', '.fade-in'], triggerPercentage: 20 });
 */
class ScrollRevealManager {
  /**
   * @param {Object} options
   * @param {string[]} [options.selectors=['.reveal-up', '.fade-in']] - Array of CSS selectors to observe.
   * @param {string} [options.visibleClass='is-visible'] - Class added when the element enters the viewport.
   * @param {number} [options.triggerPercentage=20] - Percentage from the bottom of the viewport to trigger the animation.
   */
  constructor(options = {}) {
    const defaultSelectors = [".reveal-up", ".fade-in"];
    this.selectors = options.selectors || defaultSelectors;
    this.selectorString = this.selectors.join(", ");
    this.visibleClass = options.visibleClass || "is-visible";

    // Easy to manage scroll percentage offset (20 = 20% from bottom of the screen)
    this.triggerPercentage =
      options.triggerPercentage !== undefined ? options.triggerPercentage : 20;
    this.rootMargin = `0px 0px -${this.triggerPercentage}% 0px`;

    this.observer = null;

    // Respect user's motion preferences
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return;
    }

    this.initObserver();
    this.observeExisting();
    this.initMutationObserver();
  }

  /**
   * Initializes the IntersectionObserver.
   * Triggers once per element and then unobserves it.
   */
  initObserver() {
    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add(this.visibleClass);
            this.observer.unobserve(entry.target);
          }
        }
      },
      { rootMargin: this.rootMargin },
    );
  }

  /**
   * Finds and observes all matching elements currently in the DOM.
   */
  observeExisting() {
    const els = document.querySelectorAll(this.selectorString);
    els.forEach((el) => this.observer.observe(el));
  }

  /**
   * Initializes a MutationObserver on the body to automatically watch
   * for newly added elements or clean up removed elements.
   */
  initMutationObserver() {
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        // Handle newly added elements
        for (const node of mutation.addedNodes) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            if (
              node.matches(this.selectorString) &&
              !node.classList.contains(this.visibleClass)
            ) {
              this.observer.observe(node);
            }
            // Check if added node contains any target elements
            const childEls = node.querySelectorAll(this.selectorString);
            childEls.forEach((el) => {
              if (!el.classList.contains(this.visibleClass)) {
                this.observer.observe(el);
              }
            });
          }
        }

        // Clean up removed elements to prevent memory leaks
        for (const node of mutation.removedNodes) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            if (node.matches(this.selectorString)) {
              this.observer.unobserve(node);
            }
            const childEls = node.querySelectorAll(this.selectorString);
            childEls.forEach((el) => this.observer.unobserve(el));
          }
        }
      }
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }
}

// Auto-initialize with defaults when loaded as a script.
// Change triggerPercentage to adjust when the animation starts (e.g., 10, 20, 30).
new ScrollRevealManager({ triggerPercentage: 10 });
